import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Select, { components } from "react-select";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";
import en from "react-phone-number-input/locale/en";

/* -------------------------------------------------------------------------- */
/*  Build default country options                                             */
/* -------------------------------------------------------------------------- */
export const defaultCountryOptions = [
  {
    value: "",
    label: en.ZZ,
    dial: "",
    flag: () => (
      <span role="img" aria-label="International" className="tw-mr-2">
        🌐 +
      </span>
    ),
  },
  ...getCountries().map((code) => ({
    value: code,
    label: en[code],
    dial: `+${getCountryCallingCode(code)}`,
    flag: () => (
      <img
        src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${code}.svg`}
        alt={`${en[code]} flag`}
        className="tw-w-4 tw-h-[11px] tw-mr-2"
      />
    ),
  })),
];

/* -------------------------------------------------------------------------- */
/*  react-select render helpers                                               */
/* -------------------------------------------------------------------------- */
const Option = (props) => (
  <components.Option {...props}>
    <div className="tw-flex tw-items-center">
      {props.data.flag()}
      <span>
        {props.data.label}
        {props.data.dial && ` (${props.data.dial})`}
      </span>
    </div>
  </components.Option>
);

const SingleValue = (props) => (
  <components.SingleValue {...props}>
    <div className="tw-flex tw-items-center">
      {props.data.flag()}
      <span>{props.data.dial}</span>
    </div>
  </components.SingleValue>
);

/* -------------------------------------------------------------------------- */
/*  base styles for react-select                                              */
/* -------------------------------------------------------------------------- */
const baseSelectStyles = {
  control: (provided, { isDisabled }) => ({
    ...provided,
    backgroundColor: "transparent",
    borderColor: "transparent",
    borderRadius: 0,
    borderWidth: 0, // removes 1-px default
    boxShadow: "none", // removes blue outline on focus
    "&:hover": { borderColor: "transparent" }, // removes hover border
    cursor: isDisabled ? "not-allowed" : "pointer",
    minHeight: 40,
    paddingLeft: 8,
  }),
  singleValue: (p) => ({ ...p, color: "var(--color-text)" }),
  menu: (p) => ({
    ...p,
    marginTop: 4,
    minWidth: 260,
    zIndex: 50,
    color: "black",
  }),
  dropdownIndicator: (p) => ({ ...p, padding: "0 6px" }),
  indicatorSeparator: () => ({ display: "none" }),
};

/* -------------------------------------------------------------------------- */
/*  PhoneNumber component                                                     */
/* -------------------------------------------------------------------------- */
const PhoneNumber = ({
  borderRadius = 5,
  selectedCountry,
  onSelectCountry,
  number,
  onChangeNumber,
  options = defaultCountryOptions,
  placeholder = "123-456-7890",
  inputProps = {},
  selectStyles = {},
  isSearchable = true,
  disabled = false,
  required = false,
}) => {
  /* merge react-select styles */
  const mergedSelectStyles = {
    ...baseSelectStyles,
    ...selectStyles,
    control: selectStyles.control
      ? (p, s) => ({
          ...baseSelectStyles.control(p, s),
          ...selectStyles.control(p, s),
        })
      : baseSelectStyles.control,
  };

  const [isValid, setIsValid] = useState(false);

  /* wrapper rounding */
  const radius =
    typeof borderRadius === "number" ? `${borderRadius}px` : borderRadius;

  const inputClass =
    "tw-flex-1 tw-h-10 tw-px-3 tw-bg-transparent tw-text-md " +
    "tw-border-none tw-outline-none placeholder:tw-text-gray-400 " +
    "tw-rounded-r-[inherit] tw-rounded-l-none";

  // This effect runs when 'data' changes
  useEffect(() => {
    // If there is data, the form is valid
    setIsValid(selectedCountry.value ? true : false);
  }, [selectedCountry.value]);

  return (
    <div>
      <div
        style={{ borderRadius: radius }}
        className={`tw-flex tw-items-center tw-min-w-[255px] tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:!tw-bg-gray-700 ${
          disabled ? "tw-opacity-50 tw-cursor-not-allowed" : ""
        }`}
      >
        {/* Country selector */}
        <div className="tw-min-w-[130px] dark:tw-text-white tw-border-none">
          <Select
            options={options}
            value={selectedCountry}
            onChange={onSelectCountry}
            components={{ Option, SingleValue }}
            isSearchable={isSearchable}
            isDisabled={disabled}
            styles={mergedSelectStyles}
            required={selectedCountry.value === ""}
          />
        </div>

        {/* Phone number input */}
        <input
          type="tel"
          placeholder={placeholder}
          value={number}
          onChange={(e) => {
            if (selectedCountry.value) {
              onChangeNumber(e.target.value.replace(/\D/g, ""));
            }
          }}
          disabled={disabled}
          required={required}
          {...inputProps}
          className={`${inputClass} ${inputProps.className || ""}`}
        />
      </div>
      {!isValid && (
        <small className="tw-mb-2">You must choose a country prefix</small>
      )}
    </div>
  );
};

PhoneNumber.propTypes = {
  borderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedCountry: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string,
    dial: PropTypes.string,
    flag: PropTypes.func,
  }).isRequired,
  onSelectCountry: PropTypes.func.isRequired,
  number: PropTypes.string.isRequired,
  onChangeNumber: PropTypes.func.isRequired,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  inputProps: PropTypes.object,
  selectStyles: PropTypes.object,
  isSearchable: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

export default PhoneNumber;
